import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import SEO from "../components/SEO";
import { Emblem, Tricornio } from "../components/Emblem";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen flex bg-ink relative overflow-hidden">
      <SEO title="Iniciar sesión" path="/login" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />

      {/* Left side — branding */}
      <div className="hidden lg:flex w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-ink-700 via-ink-800 to-ink" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-gcgreen/15 blur-3xl" />

        <div className="relative p-12 flex flex-col justify-between text-white w-full z-10">
          <Link to="/" className="flex items-center gap-2.5 w-fit group">
            <Emblem className="h-10 w-10 transition-transform group-hover:rotate-3" />
            <div>
              <div className="font-display font-extrabold tracking-tight">TestOpoGuardiaCivil</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gcgold font-bold font-mono">Élite · Preparación</div>
            </div>
          </Link>

          <div>
            <Tricornio className="w-14 h-9 mb-6 opacity-60" strokeColor="#d4af37" />
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-4 font-mono">Bienvenido de nuevo</div>
            <h2 className="font-display text-5xl xl:text-6xl font-extrabold leading-[0.95] tracking-tighter">
              Sigue avanzando<br />
              <span className="text-gcgold">hacia tu plaza.</span>
            </h2>
            <p className="mt-5 text-base text-slate-400 max-w-md leading-relaxed">
              Retoma exactamente donde lo dejaste. Tu progreso, estadísticas y simulacros te están esperando.
            </p>
          </div>

          <div className="text-xs text-slate-600 font-mono">© {new Date().getFullYear()} TestOpoGuardiaCivil</div>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10 w-fit">
            <Emblem className="h-9 w-9" />
            <span className="font-display font-extrabold text-white tracking-tight">TestOpoGuardiaCivil</span>
          </Link>

          <h1 className="font-display text-4xl font-extrabold text-white tracking-tighter">Iniciar sesión</h1>
          <p className="mt-2 text-sm text-slate-400">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-gcgold hover:text-gcgold-light font-semibold" data-testid="link-register">
              Regístrate gratis →
            </Link>
          </p>

          {err && <Alert variant="destructive" className="mt-6 border-red-900/50 bg-red-950/40 text-red-200" data-testid="login-error"><AlertDescription>{err}</AlertDescription></Alert>}

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Contraseña</Label>
                <Link to="/forgot-password" className="text-xs text-gcgold hover:text-gcgold-light font-medium" data-testid="forgot-password-link">¿La olvidaste?</Link>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pr-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  aria-label="Mostrar contraseña"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gcgreen hover:bg-gcgreen-light text-white font-semibold rounded-xl shadow-[0_0_40px_-8px_rgba(10,92,54,0.7)] hover:shadow-[0_0_60px_-8px_rgba(10,92,54,0.9)] transition-all group"
              data-testid="login-submit-button"
            >
              {loading ? "Entrando..." : <>Iniciar sesión <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
            </Button>
          </form>

          <div className="mt-10 p-4 rounded-xl border border-dashed border-white/10 bg-white/[0.02]">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold mb-2 font-mono">Cuenta demo</div>
            <div className="text-xs text-slate-400 font-mono">
              demo@testopoguardiacivil.com · Demo1234!
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
